/**
 * Canonical customer-facing product URLs.
 *
 * Single source of truth for building product links on the storefront. The
 * `slugify` here MUST stay identical to the backend
 * `services/seo_catalog.slugify` (Python) — canonical URLs are compared across
 * both, and any drift would cause an avoidable redirect hop.
 *
 * URL shape: /products/{name-slug}-{seo_id}
 *   - seo_id is the short immutable public id on the item (item.seo_id).
 *   - Before an item is backfilled (no seo_id) we fall back to the legacy
 *     /item/{id} route, which is supported forever — so links never break.
 */

const SLUG_MAX = 60;
const COMBINING_MARKS = /[̀-ͯ]/g; // accents left after NFKD

export function slugify(value) {
  let s = (value == null ? "" : String(value))
    .normalize("NFKD")
    .replace(COMBINING_MARKS, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
  if (s.length > SLUG_MAX) {
    let cut = s.slice(0, SLUG_MAX);
    const i = cut.lastIndexOf("-");
    if (i > 0) cut = cut.slice(0, i);
    s = cut.replace(/^-+|-+$/g, "");
  }
  return s;
}

function seoIdOf(item) {
  return String((item && (item.seo_id || item.seoId)) || "").trim();
}

function idOf(item) {
  // Accept product docs (id) AND cart/order line snapshots (item_id).
  return String((item && (item.id || item.item_id || item.itemId)) || "").trim();
}

function nameOf(item) {
  // Product docs carry `name`; cart/order line snapshots carry `item_name`.
  return (item && (item.name || item.item_name)) || "";
}

export function legacyProductPath(id) {
  const v = String(id || "").trim();
  return v ? `/item/${encodeURIComponent(v)}` : "#";
}

/** Canonical product path. Falls back to /item/{id} when seo_id is absent. */
export function productPath(item) {
  if (!item) return "#";
  const sid = seoIdOf(item);
  const id = idOf(item);
  if (!sid) return legacyProductPath(id);
  const slug = slugify(nameOf(item)) || slugify(item.category);
  return slug ? `/products/${slug}-${sid}` : `/products/${sid}`;
}

// The canonical path IS the product path — named for callers comparing the
// current location to the one true URL (redirect-to-canonical on the PDP).
export const productCanonicalPath = productPath;
